// src/pages/services/CarRental.js
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import '../../styles/ServicePage.css';
import AnimatedBackground from '../../components/AnimatedBackground';

const CarRental = () => {
    const fadeIn = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    const testimonials = [
        {
            content: "Renting a car through Excellent Travels made our family road trip to Shimla so much easier. The vehicle was clean, well-maintained, and exactly what we needed for the mountain roads.",
            name: "Suresh Patel",
            location: "Ahmedabad, India",
            avatar: "/images/testimonials/person3.jpg"
        },
        {
            content: "I was impressed by the competitive rates and the wide selection of vehicles for our Kerala trip. The pickup in Kochi and drop-off in Trivandrum was smooth and hassle-free.",
            name: "Meera Iyer",
            location: "Hyderabad, India",
            avatar: "/images/testimonials/person4.jpg"
        }
    ];

    const faqs = [
        {
            question: "What documents do I need to rent a car?",
            answer: "You'll need a valid driver's license, a credit card in the renter's name, and a passport for international rentals. Some locations may require additional documentation."
        },
        {
            question: "Is insurance included in the rental price?",
            answer: "Basic insurance is typically included, but we recommend considering additional coverage options for complete peace of mind. Our agents can help you choose the right coverage for your needs."
        },
        {
            question: "Can I pick up the car in one location and return it to another?",
            answer: "Yes, one-way rentals are available with many of our partner companies, though additional fees may apply. Please specify your needs when booking."
        },
        {
            question: "What happens if I return the car late?",
            answer: "Late returns may incur additional charges. If you need to extend your rental period, please contact us as soon as possible to make arrangements."
        }
    ];


    // Add useEffect to ensure proper scrolling
    useEffect(() => {
        // Enable scrolling when component mounts
        document.body.style.overflow = 'auto';
        document.documentElement.style.overflow = 'auto';

        // Cleanup function to ensure scrolling is enabled when component unmounts
        return () => {
            document.body.style.overflow = 'auto';
            document.documentElement.style.overflow = 'auto';
        };
    }, []);
    return (
        <motion.div
            className="service-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
        >
            {/* Add animated background */}
            <AnimatedBackground type="car-rental" />

            {/* Main content */}
            <div className="service-hero">
                <img
                    src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
                    alt="Car Rental"
                    className="service-hero-image"
                />
                <div className="service-hero-overlay">
                    <h1 className="service-title">Car Rental</h1>
                    <p className="service-subtitle">
                        Rent vehicles worldwide with our hassle-free car rental service.
                    </p>
                </div>
            </div>

            {/* Service Logo */}
            <img
                src="https://cdn-icons-png.flaticon.com/512/2087/2087670.png"
                alt="Car Rental Logo"
                className="service-logo"
            />

            <div className="service-content">
                <motion.div
                    className="service-description"
                    {...fadeIn}
                >
                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/2087/2087670.png" alt="Car Icon" className="section-icon" />
                        Freedom to Explore at Your Own Pace
                    </h2>
                    <p>
                        Excellent Travels offers convenient and affordable car rental services worldwide.
                        Whether you need a compact car for city exploration, an SUV for a family adventure,
                        or a luxury vehicle for a special occasion, we have the perfect option for you.
                    </p>
                    <p>
                        Our car rental service provides:
                    </p>
                    <ul>
                        <li>Partnerships with leading car rental companies globally</li>
                        <li>Competitive rates with no hidden fees</li>
                        <li>Wide selection of vehicles for every need and budget</li>
                        <li>Flexible pickup and drop-off locations</li>
                        <li>24/7 roadside assistance for peace of mind</li>
                    </ul>
                    <p>
                        We work with trusted rental companies to ensure you receive well-maintained vehicles
                        and excellent service wherever your travels take you.
                    </p>

                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/3097/3097144.png" alt="Vehicle Categories Icon" className="section-icon" />
                        Our Vehicle Categories
                    </h2>
                    <p>
                        We offer a diverse fleet to meet your specific needs:
                    </p>
                    <ul>
                        <li><strong>Economy & Compact:</strong> Fuel-efficient and perfect for city driving</li>
                        <li><strong>Mid-size & Full-size:</strong> Added comfort for longer journeys</li>
                        <li><strong>SUVs & Minivans:</strong> Spacious options for families and groups</li>
                        <li><strong>Luxury & Premium:</strong> High-end vehicles for a sophisticated experience</li>
                        <li><strong>Specialty Vehicles:</strong> Convertibles, electric cars, and more</li>
                    </ul>

                    <div className="feature-image-container">
                        <img
                            src="https://images.unsplash.com/photo-1546614042-7df3c24c9e5d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
                            alt="Luxury Car Fleet"
                            className="feature-image"
                        />
                        <div className="feature-image-caption">Our premium fleet includes luxury and specialty vehicles</div>
                    </div>

                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/2784/2784459.png" alt="Packages Icon" className="section-icon" />
                        Popular Rental Packages
                    </h2>
                    <p>
                        Our most popular car rental packages in India:
                    </p>
                    <ul>
                        <li><strong>Weekend Getaway:</strong> 3-day rental with 300km included - Contact for personalized quote</li>
                        <li><strong>Golden Triangle Explorer:</strong> 7-day SUV rental for Delhi-Agra-Jaipur - Contact for personalized quote</li>
                        <li><strong>Kerala Coast Drive:</strong> 10-day mid-size car with unlimited kilometers - Contact for personalized quote</li>
                        <li><strong>Himalayan Adventure:</strong> 7-day 4x4 SUV with driver-guide - Contact for personalized quote</li>
                        <li><strong>Business Travel:</strong> Luxury sedan with chauffeur, 10 hours daily - Contact for personalized quote</li>
                    </ul>

                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/2784/2784403.png" alt="Process Icon" className="section-icon" />
                        How It Works
                    </h2>
                    <p>
                        Renting a car with us is simple:
                    </p>
                    <ol>
                        <li>Specify your pickup and drop-off locations and dates</li>
                        <li>Choose your preferred vehicle category</li>
                        <li>Select any additional options (GPS, child seats, additional drivers)</li>
                        <li>Review and confirm your booking</li>
                        <li>Present your documentation at the rental counter</li>
                        <li>Enjoy your journey with the freedom of your own vehicle</li>
                    </ol>
                </motion.div>

                <motion.div
                    className="service-sidebar"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    <img
                        src="https://cdn-icons-png.flaticon.com/512/2087/2087670.png"
                        alt="Car Rental Icon"
                        className="service-sidebar-logo"
                    />

                    <h3>Why Choose Our Car Rental Service</h3>
                    <ul className="service-features">
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2933/2933266.png" alt="No Fees Icon" />
                            </span>
                            No booking fees
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2933/2933116.png" alt="Cancellation Icon" />
                            </span>
                            Free cancellation on most bookings
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2933/2933087.png" alt="Price Match Icon" />
                            </span>
                            Price match guarantee
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2921/2921222.png" alt="Pricing Icon" />
                            </span>
                            Transparent pricing
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2784/2784403.png" alt="Coverage Icon" />
                            </span>
                            Worldwide coverage
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2784/2784389.png" alt="Support Icon" />
                            </span>
                            24/7 customer support
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2921/2921195.png" alt="Rewards Icon" />
                            </span>
                            Loyalty rewards program
                        </li>
                    </ul>
                </motion.div>
            </div>

            <motion.div
                className="cta-section"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
            >
                <h2>Ready to Hit the Road?</h2>
                <p>
                    Contact our team today for personalized pricing and to find the perfect rental car for your next adventure.
                </p>
                <Link to="/contact" className="cta-button">
                    Request a Quote
                    <span className="arrow-icon">→</span>
                </Link>
            </motion.div>

            <motion.div
                className="testimonials"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
            >
                <h2>What Our Customers Say</h2>
                <div className="testimonials-grid">
                    {testimonials.map((testimonial, index) => (
                        <motion.div
                            key={index}
                            className="testimonial-card"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.7 + (index * 0.1) }}
                        >
                            <p className="testimonial-content">"{testimonial.content}"</p>
                            <div className="testimonial-author">
                                <div className="author-info">
                                    <h4>{testimonial.name}</h4>
                                    <p>{testimonial.location}</p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            <motion.div
                className="faq-section"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
            >
                <h2>Frequently Asked Questions</h2>
                {faqs.map((faq, index) => (
                    <motion.div
                        key={index}
                        className="faq-item"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.9 + (index * 0.1) }}
                    >
                        <h3 className="faq-question">{faq.question}</h3>
                        <p className="faq-answer">{faq.answer}</p>
                    </motion.div>
                ))}
            </motion.div>
        </motion.div>
    );
};

export default CarRental;